import type { Editor } from "@tiptap/core";
import type { EditorContext, Reference } from "./context";
import { button, element } from "./dom";
import { field, selectInput, textInput } from "./panels";
import { applyLink, box, humanize, insertBlock, insertInline, mapNode, PICTURE_SIZES, MAP_SIZES, pictureNode, STAGES, tag } from "./pieces";
import { searchPanel } from "./search_panel";

export interface PickerContext extends EditorContext {
	editor: Editor;
}

interface TagPicker {
	tipo: string;
	title: string;
	// What goes between the brackets for a picked record; `format` is the chosen card format, when the tag has one.
	args: (ref: Reference, format: string | null) => string;
}

// The tags a search alone settles: a sheet or a link to a record. Block tags end in BOX.
export const TAG_PICKERS: Record<string, TagPicker> = {
	MONSTERBOX: { tipo: "monstro", title: "panel_monster", args: (ref, format) => (format ? `${ref.value}, ${format}` : ref.value) },
	SPELLBOX: { tipo: "magia", title: "panel_spell", args: (ref) => ref.value },
	CHARACTERBOX: { tipo: "personagem", title: "panel_character", args: (ref) => ref.value },
	RETAINERBOX: { tipo: "ajudante", title: "panel_retainer", args: (ref) => ref.value },
	MONSTRO: { tipo: "monstro", title: "panel_link_monster", args: (ref) => ref.link ?? ref.value },
	MAGIA: { tipo: "magia", title: "panel_link_spell", args: (ref) => ref.link ?? ref.value },
	EQUIPAMENTO: { tipo: "equipamento", title: "panel_link_item", args: (ref) => ref.link ?? ref.value },
};

export const isBoxTag = (name: string) => name.endsWith("BOX");

export const formatLabel = (c: EditorContext, format: string | null) =>
	format ? (c.t(`format_${format}`) === `format_${format}` ? format : c.t(`format_${format}`)) : c.t("format_full");

// The card formats as buttons; the chosen one is what a monster picked next is inserted with.
function formatChips(c: EditorContext, state: { format: string | null }): HTMLElement {
	const wrap = element("div", "flex flex-wrap items-center gap-1.5 text-sm");
	wrap.setAttribute("role", "group");
	wrap.setAttribute("aria-label", c.t("format_title"));
	wrap.append(element("span", "text-ink-muted", c.t("format_title")));
	const chips = [null, ...c.monsterFormats].map((format) => {
		const chip = button("odo-chapter-tool border border-line font-bold", formatLabel(c, format), () => {
			state.format = format;
			for (const [other, el] of chips) el.setAttribute("aria-pressed", String(other === format));
		});
		chip.setAttribute("aria-pressed", String(state.format === format));
		return [format, chip] as const;
	});
	wrap.append(...chips.map(([, chip]) => chip));
	return wrap;
}

// Opens the search for a tag's records in the panel; `pick` gets the arguments for the tag. A card keeps the `format` it
// has (a swap) or offers the formats as buttons (`chips`, a new card).
export function tagPicker(c: EditorContext, name: string, pick: (args: string) => void, format: string | null, chips = false) {
	const picker = TAG_PICKERS[name];
	const state = { format };
	c.openPanel(c.t(picker.title), (close) =>
		searchPanel(c, {
			tipo: picker.tipo,
			label: c.t("search_label"),
			above: chips ? formatChips(c, state) : undefined,
			choose: (ref) => {
				pick(picker.args(ref, state.format));
				close();
			},
		}),
	);
}

export function insertTag(c: PickerContext, name: string) {
	const chips = name === "MONSTERBOX" && c.monsterFormats.length > 0;
	// The compact card is what the books use nearly everywhere, so it is the one on offer first.
	const pick = (args: string) => (isBoxTag(name) ? insertBlock(c.editor, box(name, args)) : insertInline(c.editor, tag(name, args)));
	tagPicker(c, name, pick, chips ? c.monsterFormats[0] : null, chips);
}

// A link to a book or a chapter (or one of its sections) over the selected text.
export function insertLink(c: PickerContext, tipo: "capitulo" | "livro") {
	c.openPanel(c.t(tipo === "capitulo" ? "panel_link_chapter" : "panel_link_book"), (close) =>
		searchPanel(c, {
			tipo,
			label: c.t("search_label"),
			choose: (ref) => {
				applyLink(c.editor, ref.value, ref.label);
				close();
			},
		}),
	);
}

export function stagePicker(c: EditorContext, pick: (level: string) => void) {
	c.openPanel(c.t("panel_stage"), (close) => {
		const list = element("div", "flex flex-col gap-2");
		for (const level of STAGES) {
			list.append(
				button("odo-button__alt w-full py-2 text-left", c.t(`stage_${level}`), () => {
					pick(level);
					close();
				}),
			);
		}
		return list;
	});
}

export const insertStage = (c: PickerContext) => stagePicker(c, (level) => insertInline(c.editor, tag("ESTAGIO", level)));

// The files of the book being edited: a map or a picture, found by name.
export function filePicker(c: EditorContext, tipo: "mapa" | "imagem", title: string, pick: (ref: Reference, close: () => void) => void) {
	c.openPanel(c.t(title), (close) => searchPanel(c, { tipo, label: c.t("search_label"), choose: (ref) => pick(ref, close) }));
}

const sizeSelect = (c: EditorContext, sizes: { cls: string; label: string }[]) =>
	selectInput(
		sizes.map((size) => ({ value: size.cls, label: c.t(size.label) })),
		sizes[0].cls,
	);

const submit = (text: string, click: () => void) => button("odo-button__primary w-full py-2", text, click);

// Picking a map leads to its form: the size and the title, with the players' file it was found with.
export function insertMap(c: PickerContext) {
	filePicker(c, "mapa", "panel_map", (ref) =>
		c.openPanel(c.t("panel_map"), (close) => {
			const form = element("div", "flex flex-col gap-2.5");
			const file = element("div", "flex flex-col gap-1 text-sm font-bold");
			file.append(c.t("map_file"), element("code", "break-all font-mono font-normal", ref.value));
			const players =
				ref.value
					.replace(/(\.\w+)$/, "-players$1")
					.split("/")
					.pop() ?? "";
			const note = element(
				"p",
				"rounded bg-verde-100 px-2.5 py-1.5 text-sm text-verde-900 dark:bg-verde-900/40 dark:text-verde-100",
				c.t("map_players_found").replace("%{file}", players),
			);
			const size = sizeSelect(c, MAP_SIZES);
			const title = textInput(humanize(ref.value));
			form.append(
				file,
				note,
				field(c.t("map_size"), size),
				field(c.t("map_title"), title),
				submit(c.t("map_submit"), () => {
					if (!title.value.trim()) return title.focus();
					insertBlock(c.editor, mapNode(ref.value, size.value, title.value));
					close();
				}),
			);
			return form;
		}),
	);
}

// A picture of the book or an upload of the chapter, with its size and the title that is also its alternative text.
export function insertPicture(c: PickerContext) {
	filePicker(c, "imagem", "panel_picture", (ref) =>
		c.openPanel(c.t("panel_picture"), (close) => {
			const form = element("div", "flex flex-col gap-2.5");
			const file = element("div", "flex flex-col gap-1 text-sm font-bold");
			file.append(c.t("picture_file"), element("code", "break-all font-mono font-normal", ref.value));
			const size = sizeSelect(c, PICTURE_SIZES);
			const title = textInput("");
			form.append(
				file,
				field(c.t("picture_size"), size),
				field(c.t("picture_title"), title),
				submit(c.t("picture_submit"), () => {
					insertInline(c.editor, pictureNode(ref.value, title.value, size.value));
					close();
				}),
			);
			return form;
		}),
	);
}
